import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { INDUSTRIES, COMPANY_SIZES } from '../types'
import type { IdentityProfile, ProductOrService, ValueProposition } from '../types'
import { LocationAutocomplete } from './LocationAutocomplete'
import { TagInput } from './TagInput'
import { extractFromDocument } from '../services/companyProfileApi'

// edit: owner — everything. read-only: admin/member — nothing editable here. (Target
// customers are no longer part of this form: owners and admins manage them as ICPs
// under Sales → ICPs.)
export type IdentityProfileFormMode = 'edit' | 'read-only'

interface Props {
  workspaceId: string
  initial: IdentityProfile | null
  saving: boolean
  mode?: IdentityProfileFormMode
  // Whether the workspace has at least one complete ICP (the profile only counts as complete
  // with one — ICPs live under Sales → ICPs). Defaults to true so an unknown never nags.
  hasCompleteIcp?: boolean
  onSave: (input: IdentityProfile) => Promise<unknown>
  onDirtyChange?: (dirty: boolean) => void
}

export type IdentityProfileFormHandle = {
  save: () => Promise<void>
  discard: () => void
}

// The profile still carries a legacy `targetCustomerProfile` for workspaces that predate
// ICPs. It is not part of this form and the API rejects it on save, so it is dropped here.
function normalizeIdentity(initial: IdentityProfile | null): IdentityProfile {
  const { targetCustomerProfile: _legacyIcp, ...rest } = initial ?? {}
  return {
    ...rest,
    productsServices: rest.productsServices ?? [],
    valuePropositions: rest.valuePropositions ?? [],
    competitors: rest.competitors ?? [],
  }
}

type SectionId = 'basics' | 'positioning' | 'products' | 'value-props' | 'differentiators' | 'competitors'

const SECTIONS: { id: SectionId; label: string }[] = [
  { id: 'basics', label: 'Basics' },
  { id: 'positioning', label: 'Positioning' },
  { id: 'products', label: 'Products & Services' },
  { id: 'value-props', label: 'Value Propositions' },
  { id: 'differentiators', label: 'Differentiators' },
  { id: 'competitors', label: 'Competitors' },
]

function isSectionComplete(id: SectionId, identity: IdentityProfile): boolean {
  switch (id) {
    case 'basics':
      return Boolean(identity.companyName && identity.website && identity.industry && identity.companySize && identity.hqLocation)
    case 'positioning':
      return Boolean(identity.oneLiner && identity.aboutUs)
    case 'products':
      return Boolean(identity.productsServices && identity.productsServices.length > 0 && identity.productsServices.every((p) => p.name && p.description))
    case 'value-props':
      return Boolean(identity.valuePropositions && identity.valuePropositions.length > 0 && identity.valuePropositions.every((v) => v.title && v.description))
    case 'differentiators':
      return Boolean(identity.differentiators)
    case 'competitors':
      return true // optional — never blocks completion
  }
}

export const IdentityProfileForm = React.forwardRef<IdentityProfileFormHandle, Props>(({ workspaceId, initial, saving, mode = 'edit', hasCompleteIcp = true, onSave, onDirtyChange }, ref) => {
  const { currentTheme: t } = useTheme()
  const [savedIdentity, setSavedIdentity] = useState<IdentityProfile>(() => normalizeIdentity(initial))
  const [identity, setIdentity] = useState<IdentityProfile>(() => normalizeIdentity(initial))
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [extracting, setExtracting] = useState(false)
  const [extractMessage, setExtractMessage] = useState<string | null>(null)
  const [extractError, setExtractError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const canEditIdentity = mode === 'edit'

  const isDirty = JSON.stringify(identity) !== JSON.stringify(savedIdentity)

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // Clear the dirty flag on unmount so a stale "true" doesn't linger if the
  // parent keeps state around after this form is no longer rendered.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

  const mandatorySections = SECTIONS.filter((s) => s.id !== 'competitors')
  const completedCount = useMemo(
    () => mandatorySections.filter((s) => isSectionComplete(s.id, identity)).length,
    [identity, mandatorySections],
  )
  const allComplete = completedCount === mandatorySections.length
  const progressPercent = Math.round((completedCount / mandatorySections.length) * 100)

  const inputStyle = {
    backgroundColor: t.inputBackground,
    color: t.text,
    borderColor: t.border,
  }

  const labelClass = 'block text-sm font-semibold mb-2'
  const inputClass = 'w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition disabled:cursor-not-allowed disabled:opacity-60'
  // Locked sections keep their values visible but drop the add/remove row buttons.
  const lockedFieldsetClass = (locked: boolean) => `min-w-0 border-0 p-0 m-0${locked ? ' [&_button]:hidden' : ''}`

  const updateField = <K extends keyof IdentityProfile>(key: K, value: IdentityProfile[K]) => {
    setIdentity((prev) => ({ ...prev, [key]: value }))
    setSavedMessage(null)
  }

  const updateProductsServices = (rows: ProductOrService[]) => updateField('productsServices', rows)
  const updateValueProps = (rows: ValueProposition[]) => updateField('valuePropositions', rows)

  // Rows added via "+ Add" but not yet filled in would otherwise fail backend
  // validation (name/description are required per row) and block the *entire*
  // save. Drop only fully-blank rows here — a half-filled row is left as-is so
  // the user still gets a clear error for it, rather than silently discarding
  // partial work.
  const buildSavePayload = (): IdentityProfile => ({
    ...normalizeIdentity(identity),
    productsServices: (identity.productsServices ?? []).filter((r) => r.name.trim() || r.description.trim()),
    valuePropositions: (identity.valuePropositions ?? []).filter((r) => r.title.trim() || r.description.trim()),
  })

  const handleSave = async () => {
    setFormError(null)
    setSavedMessage(null)
    try {
      if (mode === 'read-only') return
      await onSave(buildSavePayload())
      setSavedIdentity(identity)
      setSavedMessage(
        allComplete
          ? hasCompleteIcp
            ? 'Company profile completed.'
            : 'Company details saved. Add an ICP in Sales to finish setup.'
          : 'Progress saved.',
      )
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save. Please try again.')
    }
  }

  // Stateless on the backend — the file is parsed and sent to the model once,
  // then discarded. Nothing here saves the document or the resulting fields;
  // it only fills the draft below for the user to review and Save themselves.
  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!file) return

    setExtracting(true)
    setExtractError(null)
    setExtractMessage(null)
    try {
      // The extractor also proposes an ICP; this form no longer has one (ICPs live under
      // Sales → ICPs), so only the identity fields are applied here.
      const { targetCustomerProfile: _proposedIcp, ...extracted } = await extractFromDocument(workspaceId, file)
      const filledCount = Object.keys(extracted).length
      if (filledCount === 0) {
        setExtractMessage("Couldn't find anything usable in that document.")
      } else {
        setIdentity((prev) => ({
          ...prev,
          ...extracted,
        }))
        setExtractMessage(`Filled in ${filledCount} field${filledCount === 1 ? '' : 's'} from your document — review below before saving.`)
      }
    } catch (err) {
      setExtractError(err instanceof Error ? err.message : 'Failed to read that document. Please try again.')
    } finally {
      setExtracting(false)
    }
  }

  useImperativeHandle(ref, () => ({
    save: handleSave,
    discard: () => {
      setIdentity(savedIdentity)
      setFormError(null)
      setSavedMessage(null)
    },
  }))

  return (
    <div>
      {mode !== 'edit' && (
        <div
          className="rounded-xl px-4 py-3 mb-6 text-sm"
          style={{ backgroundColor: t.hoverBackground, color: t.secondrytext }}
        >
          You have view-only access. Only the workspace owner can edit the company profile — owners and admins manage
          target customers under Sales → ICPs.
        </div>
      )}
      {canEditIdentity && !allComplete && (
        <div
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border p-4 mb-6"
          style={{ borderColor: t.border, backgroundColor: t.hoverBackground }}
        >
          <div>
            <p className="text-sm font-semibold" style={{ color: t.text, backgroundColor: t.sectionBackground }}>
              Autofill from a document
            </p>
            <p className="text-xs mt-0.5" style={{ color: t.mutedText }}>
              Upload a pitch deck, one-pager, or about page (PDF, DOCX, TXT, MD, or CSV) and we'll fill in whatever
              fields we can find below. Nothing is saved until you review and hit Save yourself.
            </p>
          </div>
          <div className="shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md,.csv"
              className="hidden"
              onChange={(e) => void handleFileSelected(e)}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={extracting}
              className="flex items-center justify-center gap-2 h-9 px-4 rounded-xl text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
              style={{ backgroundColor: t.secondary, color: t.text, border: `1px solid ${t.border}` }}
            >
              {extracting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {extracting ? 'Reading…' : 'Upload document'}
            </button>
          </div>
        </div>
      )}
      {extractError && (
        <p className="text-xs mb-6 -mt-4" style={{ color: t.errorText }}>{extractError}</p>
      )}
      {extractMessage && !extractError && (
        <p className="text-xs mb-6 -mt-4" style={{ color: t.successText }}>{extractMessage}</p>
      )}

      {!allComplete && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold" style={{ color: t.text }}>Profile completeness</span>
          </div>
          <div className="h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: t.hoverBackground }}>
            <div
              className="h-full rounded-full transition-[width] duration-300 ease-out"
              style={{ width: `${progressPercent}%`, background: t.accentGradient }}
            />
          </div>
        </div>
      )}

      <div>
        <fieldset disabled={!canEditIdentity} className={lockedFieldsetClass(!canEditIdentity)}>
        {/* Basics */}
        <section id="section-basics" className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>Basics</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} style={{ color: t.text }}>Company name <span style={{ color: t.accent }}>*</span></label>
              <input
                value={identity.companyName ?? ''}
                onChange={(e) => updateField('companyName', e.target.value)}
                placeholder="e.g. Zeph AI"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>Website <span style={{ color: t.accent }}>*</span></label>
              <input
                value={identity.website ?? ''}
                onChange={(e) => updateField('website', e.target.value)}
                placeholder="https://example.com"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>Industry <span style={{ color: t.accent }}>*</span></label>
              <select
                value={identity.industry ?? ''}
                onChange={(e) => updateField('industry', e.target.value)}
                className={`${inputClass} appearance-none`}
                style={inputStyle}
              >
                <option value="">Select industry</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>Company size <span style={{ color: t.accent }}>*</span></label>
              <select
                value={identity.companySize ?? ''}
                onChange={(e) => updateField('companySize', e.target.value)}
                className={`${inputClass} appearance-none`}
                style={inputStyle}
              >
                <option value="">Select company size</option>
                {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass} style={{ color: t.text }}>HQ location <span style={{ color: t.accent }}>*</span></label>
              <LocationAutocomplete
                value={identity.hqLocation ?? ''}
                onChange={(value) => updateField('hqLocation', value)}
                placeholder="e.g. Bengaluru, India"
              />
            </div>
          </div>
        </section>

        {/* Positioning */}
        <section id="section-positioning" className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>Positioning</h3>
          <div className="space-y-4">
            <div>
              <label className={labelClass} style={{ color: t.text }}>
                One-liner <span style={{ color: t.accent }}>*</span>
                <span className="ml-2 font-normal" style={{ color: t.mutedText }}>{(identity.oneLiner ?? '').length}/160</span>
              </label>
              <input
                value={identity.oneLiner ?? ''}
                onChange={(e) => updateField('oneLiner', e.target.value.slice(0, 160))}
                placeholder="One sentence describing what you do"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>
                About us <span style={{ color: t.accent }}>*</span>
                <span className="ml-2 font-normal" style={{ color: t.mutedText }}>{(identity.aboutUs ?? '').length}/2000</span>
              </label>
              <textarea
                value={identity.aboutUs ?? ''}
                onChange={(e) => updateField('aboutUs', e.target.value.slice(0, 2000))}
                placeholder="A longer description of your company and what it offers"
                rows={4}
                className={inputClass}
                style={inputStyle}
              />
            </div>
          </div>
        </section>

        {/* Products & Services */}
        <section id="section-products" className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold" style={{ color: t.text }}>Products & Services <span style={{ color: t.accent }}>*</span></h3>
            <button
              type="button"
              onClick={() => updateProductsServices([...(identity.productsServices ?? []), { name: '', description: '' }])}
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition"
              style={{ backgroundColor: t.hoverBackground, color: t.text }}
            >
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="space-y-3">
            {(identity.productsServices ?? []).length === 0 && (
              <p className="text-sm" style={{ color: t.mutedText }}>Add at least one product or service.</p>
            )}
            {(identity.productsServices ?? []).map((row, idx) => (
              <div key={idx} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] items-start rounded-xl border p-3" style={{ borderColor: t.border }}>
                <input
                  value={row.name}
                  onChange={(e) => {
                    const next = [...(identity.productsServices ?? [])]
                    next[idx] = { ...row, name: e.target.value }
                    updateProductsServices(next)
                  }}
                  placeholder="Name"
                  className={inputClass}
                  style={inputStyle}
                />
                <input
                  value={row.description}
                  onChange={(e) => {
                    const next = [...(identity.productsServices ?? [])]
                    next[idx] = { ...row, description: e.target.value }
                    updateProductsServices(next)
                  }}
                  placeholder="Short description"
                  className={inputClass}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => updateProductsServices((identity.productsServices ?? []).filter((_, i) => i !== idx))}
                  className="rounded-lg p-2.5 transition hover:opacity-70"
                  style={{ color: t.errorText }}
                  aria-label="Remove"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Value Propositions */}
        <section id="section-value-props" className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold" style={{ color: t.text }}>Value Propositions <span style={{ color: t.accent }}>*</span></h3>
            <button
              type="button"
              onClick={() => updateValueProps([...(identity.valuePropositions ?? []), { title: '', description: '' }])}
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition"
              style={{ backgroundColor: t.hoverBackground, color: t.text }}
            >
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="space-y-3">
            {(identity.valuePropositions ?? []).length === 0 && (
              <p className="text-sm" style={{ color: t.mutedText }}>Add at least one value proposition.</p>
            )}
            {(identity.valuePropositions ?? []).map((row, idx) => (
              <div key={idx} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] items-start rounded-xl border p-3" style={{ borderColor: t.border }}>
                <input
                  value={row.title}
                  onChange={(e) => {
                    const next = [...(identity.valuePropositions ?? [])]
                    next[idx] = { ...row, title: e.target.value }
                    updateValueProps(next)
                  }}
                  placeholder="Title"
                  className={inputClass}
                  style={inputStyle}
                />
                <input
                  value={row.description}
                  onChange={(e) => {
                    const next = [...(identity.valuePropositions ?? [])]
                    next[idx] = { ...row, description: e.target.value }
                    updateValueProps(next)
                  }}
                  placeholder="What pain does this solve, and for whom?"
                  className={inputClass}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => updateValueProps((identity.valuePropositions ?? []).filter((_, i) => i !== idx))}
                  className="rounded-lg p-2.5 transition hover:opacity-70"
                  style={{ color: t.errorText }}
                  aria-label="Remove"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </section>

        </fieldset>

        {/* Target customers — moved out of the profile; one place to manage them now */}
        <section id="section-icp" className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-2" style={{ color: t.text }}>Target Customers</h3>
          <p className="text-sm" style={{ color: t.secondrytext }}>
            Who you sell to is now managed as one or more ideal customer profiles (ICPs) — one per segment you target.{' '}
            <Link to="/dashboard/sales?tab=icps" className="font-semibold underline underline-offset-2" style={{ color: t.accent }}>
              Manage ICPs in Sales
            </Link>
          </p>
          {!hasCompleteIcp && (
            <p
              className="mt-3 rounded-xl px-4 py-3 text-sm"
              role="status"
              style={{ backgroundColor: t.warningBackground, color: t.warningText }}
            >
              You haven’t added a complete ICP yet. Your profile isn’t finished until at least one ICP has an industry,
              company size and job title — Zeph can’t target the right customers without it.
            </p>
          )}
        </section>

        <fieldset disabled={!canEditIdentity} className={lockedFieldsetClass(!canEditIdentity)}>
        {/* Differentiators */}
        <section id="section-differentiators" className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>Differentiators <span style={{ color: t.accent }}>*</span></h3>
          <textarea
            value={identity.differentiators ?? ''}
            onChange={(e) => updateField('differentiators', e.target.value.slice(0, 1000))}
            placeholder="What makes you different from alternatives?"
            rows={3}
            className={inputClass}
            style={inputStyle}
          />
          <p className="mt-1.5 text-xs text-right" style={{ color: t.mutedText }}>{(identity.differentiators ?? '').length}/1000</p>
        </section>

        {/* Competitors (optional) */}
        <section id="section-competitors">
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>
            Competitors <span className="font-normal text-sm" style={{ color: t.mutedText }}>(optional)</span>
          </h3>
          <TagInput
            value={identity.competitors ?? []}
            onChange={(competitors) => updateField('competitors', competitors)}
            placeholder="e.g. Competitor A"
            disabled={!canEditIdentity}
          />
        </section>
        </fieldset>
      </div>

      {/* Footer */}
      <div
        className="mt-5 pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
        style={{ borderTop: `1px solid ${t.border}` }}
      >
        <div className="text-sm" style={{ color: allComplete ? (hasCompleteIcp ? t.successText : t.warningText) : t.secondrytext }}>
          {allComplete
            ? hasCompleteIcp
              ? 'All mandatory sections complete.'
              : 'Company details complete — one step left: add an ICP in Sales → ICPs.'
            : `${completedCount}/${mandatorySections.length} mandatory sections complete`}
          {formError && <span className="ml-3" style={{ color: t.errorText }}>{formError}</span>}
          {savedMessage && !formError && <span className="ml-3" style={{ color: t.successText }}>{savedMessage}</span>}
        </div>
        {canEditIdentity && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 h-10 px-5 rounded-full text-[13.5px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 shrink-0"
            style={{ background: t.accentGradient, boxShadow: '0 1px 6px rgba(79,70,229,0.25)' }}
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {saving ? 'Saving…' : 'Save Progress'}
          </button>
        )}
      </div>
    </div>
  )
})

export default React.memo(IdentityProfileForm)
